import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  LinearProgress,
  List,
  ListItemButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  MP_LABEL,
  MP_SHORT,
  issueCounts,
  linkStatsByMp,
  mpChipSx,
  severityColor,
  visibleIssues,
} from './msProductsModel';

const SEVERITY_TEXT = { error: 'ошибок', warning: 'предупреждений', info: 'подсказок' };

function MpBadges({ links }) {
  const stats = linkStatsByMp(links);
  if (!stats.length) {
    return <Typography variant="caption" color="text.disabled">нет связей</Typography>;
  }
  return (
    <Stack direction="row" spacing={0.5}>
      {stats.map((s) => (
        <Tooltip key={s.marketplace} title={`${MP_LABEL[s.marketplace] || s.marketplace}: трансляция ${s.enabled} из ${s.total}`}>
          <Box
            sx={{
              ...mpChipSx(s.marketplace),
              opacity: s.enabled ? 1 : 0.45,
              borderRadius: 1,
              px: 0.5,
              lineHeight: '18px',
              fontSize: 11,
              whiteSpace: 'nowrap',
            }}
          >
            {MP_SHORT[s.marketplace] || s.marketplace} {s.enabled}/{s.total}
          </Box>
        </Tooltip>
      ))}
    </Stack>
  );
}

export function SeverityBadge({ issues }) {
  const counts = issueCounts(issues);
  const severity = counts.error ? 'error' : counts.warning ? 'warning' : counts.info ? 'info' : null;
  if (!severity) return null;
  const title = ['error', 'warning', 'info']
    .filter((s) => counts[s])
    .map((s) => `${SEVERITY_TEXT[s]}: ${counts[s]}`)
    .join(', ');
  const total = counts.error + counts.warning + counts.info;
  return (
    <Tooltip title={title}>
      <Chip
        size="small"
        color={severityColor(severity)}
        variant={severity === 'info' ? 'outlined' : 'filled'}
        label={total}
        sx={{ height: 20, minWidth: 24, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }}
      />
    </Tooltip>
  );
}

function rowSubtitle(row) {
  const article = (row.links || []).map((l) => l.article).find(Boolean);
  return [article, row.barcode].filter(Boolean).join(' · ') || row.ms_id;
}

export default function MsProductList({
  rows,
  count,
  loading,
  selectedId,
  onSelect,
  checkedIds,
  onToggleCheck,
  onToggleCheckAll,
  onLoadMore,
  emptyText,
}) {
  const allChecked = rows.length > 0 && rows.every((row) => checkedIds.has(row.ms_id));
  const someChecked = rows.some((row) => checkedIds.has(row.ms_id));
  return (
    <Card variant="outlined">
      <Stack direction="row" alignItems="center" sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
        <Checkbox
          size="small"
          checked={allChecked}
          indeterminate={someChecked && !allChecked}
          disabled={!rows.length}
          onChange={(e) => onToggleCheckAll(e.target.checked)}
          inputProps={{ 'aria-label': 'Отметить все загруженные' }}
        />
        <Typography variant="caption" color="text.secondary">
          {loading && !rows.length ? 'Загрузка…' : `Показано ${rows.length} из ${count}`}
        </Typography>
      </Stack>
      {loading && <LinearProgress />}
      {!loading && rows.length === 0 ? (
        <CardContent>
          <Typography variant="body2" color="text.secondary" align="center">{emptyText}</Typography>
        </CardContent>
      ) : (
        <List dense disablePadding sx={{ maxHeight: { md: '70vh' }, overflow: 'auto' }}>
          {rows.map((row) => {
            const archived = !!row.ms_archived;
            return (
              <ListItemButton
                key={row.ms_id}
                divider
                selected={row.ms_id === selectedId}
                onClick={() => onSelect(row.ms_id)}
                sx={{ pl: 0.5, alignItems: 'center' }}
              >
                <Checkbox
                  size="small"
                  checked={checkedIds.has(row.ms_id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => onToggleCheck(row.ms_id, e.target.checked)}
                  inputProps={{ 'aria-label': `Отметить ${row.name || row.ms_id}` }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      title={row.name || ''}
                      color={archived ? 'text.disabled' : 'text.primary'}
                      sx={{ minWidth: 0 }}
                    >
                      {row.name || '—'}
                    </Typography>
                    {archived && (
                      <Chip size="small" label="архив" sx={{ height: 18, fontSize: 10, flexShrink: 0, '& .MuiChip-label': { px: 0.5 } }} />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {rowSubtitle(row)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
                  <MpBadges links={row.links} />
                  <SeverityBadge issues={visibleIssues(row)} />
                </Stack>
              </ListItemButton>
            );
          })}
          {rows.length < count && (
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button size="small" onClick={onLoadMore} disabled={loading}>
                Показать ещё ({count - rows.length})
              </Button>
            </Box>
          )}
        </List>
      )}
    </Card>
  );
}
